import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import jwt_decode from 'jwt-decode';
import { setUserSearchSettings } from 'libs/core/data/stores/search-settings';
import {
  resetAuth,
  setClientId,
  setInvitationCode,
} from 'libs/shared/data/stores/auth/auth.actions';
import { AuthUser } from 'libs/shared/data/stores/auth/auth.models';
import { getAuthUser, getClientId } from 'libs/shared/data/stores/auth/auth.selectors';
import { variables } from 'libs/shared/utils/variables/variables';
import { map, Observable, take } from 'rxjs';

import { SystemService, UserService } from '@offconon/core-api';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private router = inject(Router);
  private userService = inject(UserService);
  private systemService = inject(SystemService);
  private activatedRoute = inject(ActivatedRoute);

  private destroyRef = inject(DestroyRef);
  private store$ = inject(Store);

  currentUserSubject = this.store$.select(getAuthUser);
  currentUser: Observable<AuthUser>;
  currentUserValue: AuthUser | null;
  private clientId: string | boolean | null = null;

  constructor() {
    this.currentUserSubject.subscribe((user: any) => {
      this.currentUserValue = user;
    });
    this.store$.select(getClientId).subscribe((clientId: any) => {
      if (clientId) this.clientId = clientId;
    });
    this.activatedRoute.queryParams.pipe(takeUntilDestroyed()).subscribe({
      next: (params) => {
        if (params[variables.inviteCode]) {
          this.store$.dispatch(setInvitationCode({ invitationCode: params[variables.inviteCode] }));
        }
      },
    });
  }

  logout() {
    this.store$.dispatch(resetAuth());
  }

  checkClientId() {
    if (!this.clientId) {
      this.systemService
        .systemGenerateguesttokenCreate()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data: any) => {
            this.store$.dispatch(
              setClientId({
                clientId: data.user_token,
              }),
            );

            this.store$.dispatch(setUserSearchSettings({ searchSettings: data }));
          },
        });
    }
  }

  isTokenExpired() {
    if (this.currentUserValue) {
      const jwtObject: any = jwt_decode(this.currentUserValue.jwt_token);
      const now = new Date();
      const exp = new Date(jwtObject.exp * 1000);
      if (exp < now) {
        return true;
      }
      return false;
    }
    return false;
  }

  handleExpiredToken() {
    if (this.isTokenExpired()) {
      this.logout();
    }
  }
  getClientId() {
    return this.clientId;
  }

  createErpLoginUrl(link: string): Promise<string> {
    return new Promise((resolve) => {
      this.store$
        .select(getAuthUser)
        .pipe(
          map((user) => {
            return {
              is_verified: user?.is_verified,

              full_name: user?.full_name,
              id: user?.id,
              username: user?.username,
              jwt_token: user?.jwt_token,
              refresh_token: user?.refresh_token,
            };
          }),
          take(1),
        )
        .subscribe((res) => {
          resolve(`${link}?${variables.auth}=${JSON.stringify(res)}`);
        });
    });
  }
  createErpLoginUrl$(link: string) {
    return this.store$.select(getAuthUser).pipe(
      map((user) => {
        return {
          is_verified: user?.is_verified,

          full_name: user?.full_name,
          id: user?.id,
          username: user?.username,
          jwt_token: user?.jwt_token,
          refresh_token: user?.refresh_token,
        };
      }),
      map((res) => `${link}?auth=${JSON.stringify(res)}`),
      take(1),
    );
  }
}
